import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router-dom'

import { UserRole, Permission, hasPermission } from '../utils/permissions'
import { useAuth } from './AuthContext'
import OrgContext from './OrgContext'
import { projectsAPI } from '../services/api'
import {
  buildUseCaseRecovery,
  buildUseCaseProjectUrl,
  USE_CASE_PROJECT_ID,
  USE_CASE_PROJECT_NAME,
  getCurrentRouteLocation,
  getRouteProjectId,
  hasUseCaseIntent,
  isUseCaseRoute,
  isLikelyQaProject,
  isUseCaseProjectKnownInaccessible,
  markUseCaseProjectInaccessible,
  replaceCurrentRouteWithUseCaseProject,
  resetUseCaseProjectAccessCache,
  type UseCaseRecoveryContext,
  type RouteLocationLike,
} from './projectUseCaseRouting'
import {
  getProjectAccessMode,
  isProjectReadOnlyTemplate,
  normalizeProjectAccessMetadata,
  type ProjectAccessMode,
} from './projectAccess'
import { useProjectMemberActions } from './useProjectMemberActions'
import type {
  Project,
  ProjectCapabilitySnapshot,
  ProjectContextType,
} from './ProjectContextTypes'

export {
  buildUseCaseProjectUrl,
  USE_CASE_PROJECT_ID,
  USE_CASE_PROJECT_NAME,
  getProjectAccessMode,
  hasUseCaseIntent,
  isUseCaseRoute,
  isLikelyQaProject,
  isProjectReadOnlyTemplate,
  normalizeProjectAccessMetadata,
}
export type { UseCaseRecoveryContext } from './projectUseCaseRouting'
export type { ProjectAccessMode } from './projectAccess'
export type {
  Project,
  ProjectCapabilitySnapshot,
  ProjectContextType,
  ProjectMember,
} from './ProjectContextTypes'

const ProjectContext = createContext<ProjectContextType | undefined>(undefined)

const useOptionalRouteLocation = (): RouteLocationLike => {
  try {
    const location = useLocation()
    return {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    }
  } catch {
    return getCurrentRouteLocation()
  }
}

export const useProject = (): ProjectContextType => {
  const context = useContext(ProjectContext)
  if (context === undefined) {
    throw new Error('useProject must be used within ProjectProvider')
  }
  return context
}

interface ProjectProviderProps {
  children: ReactNode
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({
  children,
}) => {
  const [currentProject, setCurrentProject] = useState<Project | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [autoSwitchedProject, setAutoSwitchedProject] = useState<string | null>(
    null
  )
  const [useCaseRecovery, setUseCaseRecovery] =
    useState<UseCaseRecoveryContext | null>(null)
  const [projectCapabilitySnapshots, setProjectCapabilitySnapshots] = useState<
    Record<string, ProjectCapabilitySnapshot>
  >({})
  const {
    currentUser,
    userProfile,
    updateSelectedProject,
    loading: authLoading,
  } = useAuth()
  // Phase 1 / P4 (2026-06-03): currentOrg scope rehydrates ProjectContext
  // whenever the active org changes (after switchOrg or create).
  // We read OrgContext directly via React.useContext so the absence of an
  // OrgProvider (e.g. existing tests that mount ProjectProvider alone)
  // doesn't throw — we just degrade to "no org scope" behavior.
  const orgCtx = useContext(OrgContext)
  const currentOrgId: string | null = orgCtx?.currentOrg?.id || null
  const routeLocation = useOptionalRouteLocation()
  const routePathname = routeLocation.pathname || '/'
  const routeSearch = routeLocation.search || ''
  const routeHash = routeLocation.hash || ''
  // Only watch the ?projectId= param, not the full routeSearch, so that
  // focus-only URL updates (?step=, ?class=, ?workflow=) don't trigger a
  // full project reload + loading spinner that unmounts the active panel.
  const routeProjectIdParam = new URLSearchParams(routeSearch).get('projectId')
  const updateSelectedProjectRef = useRef(updateSelectedProject)
  // 2026-06-14: track latest currentProject so reloadProjects (which is a
  // useCallback with stable deps) can preserve a locally-bootstrapped project
  // when the backend GET /api/projects hasn't indexed it yet — fixes the
  // AI Build 0→1 race where headerChip/mode banner reset after orchestrate.
  const currentProjectRef = useRef<Project | null>(null)
  // 데모 유저는 currentUser가 없지만 userProfile.email이 있음
  const currentUserEmail = currentUser?.email || userProfile?.email || null
  const currentUserName =
    userProfile?.displayName ||
    currentUser?.displayName ||
    currentUser?.email ||
    '알 수 없는 사용자'

  useEffect(() => {
    updateSelectedProjectRef.current = updateSelectedProject
  }, [updateSelectedProject])

  useEffect(() => {
    currentProjectRef.current = currentProject
  }, [currentProject])

  const reloadProjects = useCallback(async () => {
    setLoading(true)
    setError(null)
    setUseCaseRecovery(null)
    try {
      // 백엔드에서 프로젝트 로드
      const response = await projectsAPI.getAllProjects()
      let projectsFromAPI = (response.data as Project[]).map(
        normalizeProjectAccessMetadata
      )
      const currentRoute = {
        pathname: routePathname,
        search: routeSearch,
        hash: routeHash,
      }
      const linkedProjectId = getRouteProjectId(currentRoute)
      const useCaseRoute = isUseCaseRoute(currentRoute)
      const useCaseIntent = hasUseCaseIntent(currentRoute)

      const fetchProjectIntoList = async (
        projectId: string,
        options?: { forceUseCaseRecheck?: boolean }
      ) => {
        if (projectsFromAPI.some((p) => p.id === projectId)) return
        // T010 (2026-06-07): short-circuit on a previously cached 403 for the
        // shared use-case project to keep the dev console clean for users
        // without demo access (e.g. QA persona). Other project ids still hit
        // the API normally — this guard is scoped to USE_CASE_PROJECT_ID.
        // R017 E3: the flag now persists in localStorage (login-screen 404
        // noise). An explicit `?projectId=project-use-case` deep link passes
        // `forceUseCaseRecheck` so access can be re-established later.
        if (
          projectId === USE_CASE_PROJECT_ID &&
          !options?.forceUseCaseRecheck &&
          isUseCaseProjectKnownInaccessible()
        ) {
          return
        }
        try {
          const linkedProjectResponse = await projectsAPI.getProject(projectId)
          const linkedProject = normalizeProjectAccessMetadata(
            linkedProjectResponse.data as Project
          )
          if (projectId === USE_CASE_PROJECT_ID) {
            // 접근 성공 — 과거의 '접근 불가' 영구 캐시를 해제한다.
            resetUseCaseProjectAccessCache()
          }
          projectsFromAPI = [...projectsFromAPI, linkedProject]
        } catch (linkedProjectError: any) {
          const status =
            linkedProjectError?.status ??
            linkedProjectError?.response?.status
          // T026 (2026-06-07): widen the cache predicate from `status === 403`
          // to any 4xx. In dev DBs the shared use-case project row may not
          // exist at all, so the backend returns 404 (not 403) and without
          // this widening the cache never engaged — the original QA console
          // noise (use-case GET refiring on every nav) reappeared just with a
          // different status code. Treat any client-side reject (403/404/etc.)
          // as "inaccessible for this session, stop refetching."
          if (
            projectId === USE_CASE_PROJECT_ID &&
            typeof status === 'number' &&
            status >= 400 &&
            status < 500
          ) {
            markUseCaseProjectInaccessible()
          }
          console.warn('딥링크 프로젝트 조회 실패:', linkedProjectError)
        }
      }

      if (linkedProjectId) {
        // 명시적 ?projectId= 딥링크는 캐시된 '접근 불가' 를 무시하고
        // 재확인한다 (R017 E3 — 복구 동선 유지).
        await fetchProjectIntoList(linkedProjectId, {
          forceUseCaseRecheck: true,
        })
      } else if (useCaseIntent) {
        await fetchProjectIntoList(USE_CASE_PROJECT_ID)
      }

      // 2026-06-14: preserve locally-bootstrapped projects (e.g. AI Build 0→1
      // path) that may not yet appear in the API response due to backend
      // indexing lag. Without this, currentProject gets clobbered to null/first
      // and TopNav chip + AIBuild mode banner regress to "no project".
      const bootstrappedActive = currentProjectRef.current
      const apiHasBootstrapped = bootstrappedActive
        ? projectsFromAPI.some((p) => p.id === bootstrappedActive.id)
        : true
      if (bootstrappedActive && !apiHasBootstrapped) {
        projectsFromAPI = [...projectsFromAPI, bootstrappedActive]
      }
      let selectableProjects = projectsFromAPI.filter((p) => p.id !== 'GLOBAL')
      setProjects(projectsFromAPI)

      if (linkedProjectId) {
        const linkedProject = selectableProjects.find(
          (p) => p.id === linkedProjectId
        )
        if (linkedProject) {
          setCurrentProject(linkedProject)
          setUseCaseRecovery(null)
          if (linkedProject.id === USE_CASE_PROJECT_ID) {
            replaceCurrentRouteWithUseCaseProject()
          }
          return
        }
      }

      if (useCaseIntent) {
        const demoProject = selectableProjects.find(
          (p) => p.id === USE_CASE_PROJECT_ID
        )
        if (demoProject) {
          setCurrentProject(demoProject)
          setUseCaseRecovery(null)
          replaceCurrentRouteWithUseCaseProject()
          return
        }
      }

      // 사용자 프로필에서 선택된 프로젝트 확인
      // Round 6 Wave 6B P0-A fix: dev-login 서버가 발급한 selectedProjectId
      // (예: project-e2e-demo)가 사용자 accessible 목록에 실제 존재하면
      // 이는 stale preference가 아니라 의도된 선택이므로 honor 한다.
      // isLikelyQaProject 필터는 "use case 경로에서만" 적용 — 비-demo 경로
      // (예: '/', '/data', '/ontology')에서는 항상 사용자의 선택을 우선한다.
      const selectedProjectId = userProfile?.preferences?.selectedProjectId
      const selectedProject = selectedProjectId
        ? selectableProjects.find((p) => p.id === selectedProjectId)
        : null
      if (selectedProject) {
        const shouldDeferToUseCaseFallback =
          useCaseRoute && isLikelyQaProject(selectedProject)
        if (!shouldDeferToUseCaseFallback) {
          setCurrentProject(selectedProject)
          setUseCaseRecovery(null)
          return // 사용자 선택 프로젝트가 있으면 여기서 종료
        }
      }

      if (useCaseRoute) {
        await fetchProjectIntoList(USE_CASE_PROJECT_ID)
        selectableProjects = projectsFromAPI.filter((p) => p.id !== 'GLOBAL')
        setProjects(projectsFromAPI)
        const demoProject = selectableProjects.find(
          (p) => p.id === USE_CASE_PROJECT_ID
        )
        if (demoProject) {
          setCurrentProject(demoProject)
          setUseCaseRecovery(null)
          replaceCurrentRouteWithUseCaseProject()
          return
        }

        setCurrentProject(null)
        setUseCaseRecovery(
          buildUseCaseRecovery('demo_project_unavailable')
        )
        return
      }

      // 사용자 선택 프로젝트가 없거나 찾을 수 없는 경우 첫 번째 프로젝트 선택
      if (selectableProjects.length > 0) {
        // 2026-06-14: if the active currentProject is still in the merged list
        // (e.g. AI Build bootstrap mid-flight), keep it instead of forcing a
        // switch to selectableProjects[0]. Otherwise headerChip/mode banner
        // flip away from the freshly-bootstrapped project mid-orchestrate.
        const activeProject = currentProjectRef.current
        const preservedActive =
          activeProject && selectableProjects.some((p) => p.id === activeProject.id)
            ? activeProject
            : null
        const nextProject = preservedActive ?? selectableProjects[0]

        setCurrentProject(nextProject)
        setUseCaseRecovery(null)
        // 사용자 정보에 저장 (이미 active였던 프로젝트면 중복 호출 회피)
        if (
          !preservedActive &&
          updateSelectedProjectRef.current &&
          !isProjectReadOnlyTemplate(nextProject)
        ) {
          await updateSelectedProjectRef.current(nextProject.id)
        }
      } else {
        setCurrentProject(null)
        setUseCaseRecovery(null)
      }
    } catch (err: any) {
      console.error('프로젝트 초기화 실패:', err)
      // 에러 메시지 설정 - 가짜 프로젝트 생성하지 않음
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        '백엔드 서버에 연결할 수 없습니다'
      setError(`프로젝트 로드 실패: ${errorMessage}`)
      setProjects([])
      setCurrentProject(null)
      setUseCaseRecovery(null)
    } finally {
      setLoading(false)
    }
  }, [
    routeHash,
    routePathname,
    routeProjectIdParam,
    userProfile?.preferences?.selectedProjectId,
    // Phase 1 / P4 (2026-06-03): re-trigger when the active org changes so
    // backend's ``?orgId=`` scoping (via JWT claim) is honored without a
    // manual reload.
    currentOrgId,
  ])

  useEffect(() => {
    // 인증 로딩 중이면 대기
    if (authLoading) return

    const email = currentUser?.email || userProfile?.email
    if (!email) {
      setProjects([])
      setCurrentProject(null)
      setError(null)
      setUseCaseRecovery(
        isUseCaseRoute({
          pathname: routePathname,
          search: routeSearch,
          hash: routeHash,
        })
          ? buildUseCaseRecovery('auth_required')
          : null
      )
      setLoading(false)
      return
    }

    reloadProjects()
  }, [authLoading, currentUser?.email, userProfile?.email, reloadProjects])

  const handleSetCurrentProject = async (project: Project | null) => {
    const normalizedProject = project
      ? normalizeProjectAccessMetadata(project)
      : null
    setCurrentProject(normalizedProject)
    if (normalizedProject) {
      setUseCaseRecovery(null)
    }
    if (
      normalizedProject &&
      updateSelectedProject &&
      !isProjectReadOnlyTemplate(normalizedProject)
    ) {
      // 선택한 프로젝트를 사용자 정보에 저장하여 고정
      try {
        await updateSelectedProject(normalizedProject.id)
      } catch (error) {
        console.error('프로젝트 설정 저장 실패:', error)
      }
    }
  }

  const refreshProjectCapabilitySnapshot = useCallback(
    async (projectId?: string) => {
      const targetProjectId = projectId || currentProject?.id
      if (!targetProjectId) {
        return null
      }

      try {
        const response = await projectsAPI.getMyRole(targetProjectId)
        const snapshot = response.data as ProjectCapabilitySnapshot
        setProjectCapabilitySnapshots((prev) => ({
          ...prev,
          [targetProjectId]: snapshot,
        }))
        return snapshot
      } catch (err: any) {
        if (err.response?.status === 403) {
          setProjectCapabilitySnapshots((prev) => {
            const next = { ...prev }
            delete next[targetProjectId]
            return next
          })
          return null
        }
        throw err
      }
    },
    [currentProject?.id]
  )

  // 현재 사용자의 프로젝트 내 역할 가져오기
  const getCurrentUserRole = (projectId: string): UserRole | null => {
    const snapshotRole = projectCapabilitySnapshots[projectId]?.role
    if (snapshotRole) {
      return snapshotRole
    }

    const project = projects.find((p) => p.id === projectId)
    if (!project) return null
    if (isProjectReadOnlyTemplate(project)) {
      return 'viewer'
    }

    if (!currentUserEmail) {
      return null
    }

    if (project.owner === currentUserEmail) {
      return 'owner'
    }

    const member = project.members.find((m) => m.email === currentUserEmail)
    return member?.role || null
  }

  // 현재 프로젝트에서의 사용자 역할
  const currentUserRole = currentProject
    ? getCurrentUserRole(currentProject.id)
    : null
  const currentCapabilitySnapshot = currentProject
    ? (projectCapabilitySnapshots[currentProject.id] ?? null)
    : null
  const currentUserPermissions =
    currentCapabilitySnapshot?.permissionMatrix ?? null
  const currentCapabilities = currentCapabilitySnapshot?.capabilities ?? []
  const currentPrincipalType = currentCapabilitySnapshot?.principalType ?? null
  const currentAuthSource = currentCapabilitySnapshot?.authSource ?? null

  const hasProjectPermission = useCallback(
    (module: keyof Permission, action: string, projectId?: string): boolean => {
      const targetProjectId = projectId || currentProject?.id
      const targetProject = targetProjectId
        ? projects.find((p) => p.id === targetProjectId) ||
          (currentProject?.id === targetProjectId ? currentProject : undefined)
        : currentProject
      if (isProjectReadOnlyTemplate(targetProject)) {
        return ['read', 'view', 'list', 'export'].includes(action)
      }

      const snapshot = targetProjectId
        ? projectCapabilitySnapshots[targetProjectId]
        : null
      const permissionMatrix = snapshot?.permissionMatrix
      const modulePermissions = permissionMatrix?.[module] as
        | Record<string, boolean>
        | undefined

      if (modulePermissions && action in modulePermissions) {
        return Boolean(modulePermissions[action])
      }

      const fallbackRole = targetProjectId
        ? getCurrentUserRole(targetProjectId)
        : currentUserRole
      return fallbackRole ? hasPermission(fallbackRole, module, action) : false
    },
    [currentProject, currentUserRole, projectCapabilitySnapshots, projects]
  )

  const { addProjectMember, updateMemberRole, removeMember } =
    useProjectMemberActions({
      projects,
      setProjects,
      currentProject,
      setCurrentProject,
      currentUserEmail,
      getCurrentUserRole,
    })

  // 권한이 있는 프로젝트 목록 (memoized)
  // 데모 유저는 currentUser가 없지만 userProfile이 있음
  const accessibleProjects = useMemo(() => {
    const email = currentUser?.email || userProfile?.email
    if (!email) return []

    return projects.filter((p) => p.id !== 'GLOBAL')
  }, [projects, currentUser?.email, userProfile?.email])

  useEffect(() => {
    if (authLoading) return
    if (!currentProject?.id) return

    refreshProjectCapabilitySnapshot(currentProject.id).catch((err) => {
      console.error('프로젝트 capability snapshot 로드 실패:', err)
    })
  }, [authLoading, currentProject?.id, refreshProjectCapabilitySnapshot])

  // 현재 선택된 프로젝트가 접근 가능한 프로젝트 목록에 있는지 확인하고 자동 조정
  // 데모 유저는 currentUser가 없지만 userProfile이 있음
  useEffect(() => {
    const email = currentUser?.email || userProfile?.email
    if (!email) {
      // 로그인하지 않은 경우 현재 프로젝트를 null로 설정
      if (currentProject) {
        setCurrentProject(null)
      }
      return
    }

    // accessibleProjects가 비어있으면 아직 로딩 중이므로 실행하지 않음
    if (accessibleProjects.length === 0) {
      return
    }

    if (currentProject) {
      // 현재 선택된 프로젝트가 접근 가능한 프로젝트 목록에 있는지 확인
      const isCurrentProjectAccessible = accessibleProjects.some(
        (p) => p.id === currentProject.id
      )

      if (!isCurrentProjectAccessible) {
        // 접근 불가능한 프로젝트가 선택되어 있는 경우에만 변경
        const selectedProjectId = userProfile?.preferences?.selectedProjectId
        const savedProject = accessibleProjects.find(
          (p) => p.id === selectedProjectId
        )
        const fallbackProject = savedProject || accessibleProjects[0]

        if (fallbackProject) {
          setCurrentProject(fallbackProject)
          if (updateSelectedProject) {
            updateSelectedProject(fallbackProject.id)
          }
          setAutoSwitchedProject(fallbackProject.name)
        }
      }
    }
    // 현재 프로젝트가 null인 경우는 초기화 함수에서 처리하므로 여기서는 제외
  }, [
    accessibleProjects,
    currentProject,
    currentUser?.email,
    userProfile?.email,
    userProfile?.preferences?.selectedProjectId,
    updateSelectedProject,
  ])

  const clearAutoSwitchedProject = useCallback(() => {
    setAutoSwitchedProject(null)
  }, [])

  const value: ProjectContextType = {
    currentProject,
    setCurrentProject: handleSetCurrentProject,
    projects,
    accessibleProjects,
    setProjects,
    reloadProjects,
    loading,
    error,
    currentUserRole,
    currentUserPermissions,
    currentCapabilities,
    currentPrincipalType,
    currentAuthSource,
    getCurrentUserRole,
    hasProjectPermission,
    refreshProjectCapabilitySnapshot,
    addProjectMember,
    updateMemberRole,
    removeMember,
    autoSwitchedProject,
    clearAutoSwitchedProject,
    useCaseRecovery,
  }

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  )
}

export default ProjectContext
